import { type FormatContext, panelContext } from "./context";

/**
 * A HORA QUE A PESSOA ESCREVEU É A HORA DO WORKSPACE — nunca a do browser.
 *
 * ── O BUG QUE ESTE FICHEIRO EXISTE PARA FECHAR ────────────────────────────
 *
 * Um `<input type="datetime-local">` não carrega fuso: devolve "2026-08-30T09:00"
 * e mais nada. `new Date(...)` lê essa string na hora local de QUEM ESCREVEU, e
 * `.toISOString()` grava o instante que daí sai. Num browser em UTC, "9h" virava
 * `09:00Z` no banco — e o calendário, que desenha as horas no fuso do TENANT
 * (ADR-018, `features/records/calendar.ts`), mostrava 6h. O registo das 9 às 10
 * aparecia das 6 às 7, e o banco não estava errado: as duas pontas simplesmente
 * falavam de fusos diferentes.
 *
 * A regra é a mesma que `intl.ts` já aplica ao dinheiro e às datas: o fuso é um
 * facto do NEGÓCIO, não de quem abriu a aba. Um colega em Lisboa que marca uma
 * consulta às 9h marca as 9h da clínica.
 *
 * ── A VOLTA COMPLETA ──────────────────────────────────────────────────────
 *
 * `wallClock` leva um instante ao mostrador do workspace; `instantOf` traz o
 * mostrador de volta a um instante. Uma é a inversa da outra, e é isso que o
 * teste prova: o que se escreve na caixa é o que se lê na caixa, e o que fica no
 * banco é o momento que aquele mostrador nomeia naquele fuso.
 */

const parts = new Map<string, Intl.DateTimeFormat>();

/**
 * `hourCycle: "h23"` e não `hour12: false`: a segunda forma devolve "24" à
 * meia-noite em alguns motores, e uma meia-noite lida como hora 24 salta um dia.
 */
function partsIn(timeZone: string): Intl.DateTimeFormat {
	const hit = parts.get(timeZone);
	if (hit) return hit;
	const made = new Intl.DateTimeFormat("en-CA", {
		timeZone,
		hourCycle: "h23",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
	parts.set(timeZone, made);
	return made;
}

interface Clock {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
}

/** O mostrador de um instante num fuso — os seis números, já em número. */
function clockOf(at: Date, timeZone: string): Clock {
	const found = partsIn(timeZone).formatToParts(at);
	const read = (type: Intl.DateTimeFormatPartTypes): number =>
		Number(found.find((p) => p.type === type)?.value ?? Number.NaN);
	return {
		year: read("year"),
		month: read("month"),
		day: read("day"),
		hour: read("hour"),
		minute: read("minute"),
		second: read("second"),
	};
}

/**
 * Quanto o fuso adianta o relógio face a UTC, NAQUELE instante.
 *
 * Perguntado por instante e não por zona porque um fuso com horário de verão tem
 * dois deslocamentos por ano. O Brasil já não tem, mas um workspace em Lisboa
 * tem, e a conta não pode depender disso.
 */
function offsetMs(at: Date, timeZone: string): number {
	const c = clockOf(at, timeZone);
	if (Number.isNaN(c.year)) return 0;
	const asIfUtc = Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second);
	// Os milissegundos ficam de fora dos dois lados: `formatToParts` não os traz.
	return asIfUtc - Math.floor(at.getTime() / 1000) * 1000;
}

function pad(n: number, size = 2): string {
	return String(n).padStart(size, "0");
}

function instant(value: string | Date | null | undefined): Date | null {
	if (value === null || value === undefined || value === "") return null;
	const at = value instanceof Date ? value : new Date(value);
	return Number.isNaN(at.getTime()) ? null : at;
}

/**
 * Um instante no formato que o `datetime-local` quer: "AAAA-MM-DDTHH:MM", sem
 * fuso e sem segundos, já no mostrador do workspace.
 *
 * Uma data inválida (ou ausente) devolve string vazia, que é a caixa em branco —
 * nunca "Invalid Date" dentro do campo.
 */
export function wallClock(
	value: string | Date | null | undefined,
	ctx: FormatContext = panelContext(),
): string {
	const at = instant(value);
	if (!at) return "";
	const c = clockOf(at, ctx.timeZone);
	if (Number.isNaN(c.year)) return "";
	return `${pad(c.year, 4)}-${pad(c.month)}-${pad(c.day)}T${pad(c.hour)}:${pad(c.minute)}`;
}

/**
 * O caminho de volta: o mostrador que a pessoa escreveu, lido NO FUSO DO
 * WORKSPACE, como instante ISO com o `Z` que o banco espera.
 *
 * Duas passagens de propósito. A primeira supõe que a string é UTC e pergunta
 * que deslocamento o fuso tinha por ali; a segunda repete a pergunta já no
 * instante estimado. Só diferem nas poucas horas à volta de uma virada de
 * horário de verão — e é exatamente aí que uma passagem só erra uma hora.
 */
export function instantOf(
	local: string | null | undefined,
	ctx: FormatContext = panelContext(),
): string | null {
	if (!local) return null;
	const typed = local.slice(0, 16);
	if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(typed)) return null;
	const asIfUtc = Date.parse(`${typed}:00Z`);
	if (Number.isNaN(asIfUtc)) return null;
	const first = asIfUtc - offsetMs(new Date(asIfUtc), ctx.timeZone);
	const exact = asIfUtc - offsetMs(new Date(first), ctx.timeZone);
	return new Date(exact).toISOString();
}
